// src/features/secret/SanListEditor.tsx
// Chip/tag editor for SAN (Subject Alternative Name) lists and similar string lists (PKI role
// allowed domains). Replaces comma-separated text blobs with add-on-Enter/comma, remove-via-×
// chips. Chips render in mono — SANs/domains are technical identifiers (docs/ux/14-forms.md).

import { useState, type KeyboardEvent } from 'react';
import { cn } from '@core/ui';

export interface SanListEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  'aria-label'?: string;
  id?: string;
  className?: string;
}

export function SanListEditor({ value, onChange, placeholder, id, className, 'aria-label': ariaLabel }: SanListEditorProps) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (!value.includes(trimmed)) onChange([...value, trimmed]);
    setDraft('');
  };

  const removeAt = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      removeAt(value.length - 1);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1.5 rounded-md border border-dashed border-input bg-transparent px-2 py-1.5',
        'focus-within:border-solid focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30',
        className
      )}
    >
      {value.map((item, idx) => (
        <span
          key={`${item}-${idx}`}
          className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground"
        >
          {item}
          <button
            type="button"
            onClick={() => removeAt(idx)}
            className="text-muted-foreground hover:text-foreground"
            aria-label={`Remove ${item}`}
          >
            ×
          </button>
        </span>
      ))}
      <input
        id={id}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : undefined}
        aria-label={ariaLabel || placeholder}
        className="min-w-[8ch] flex-1 bg-transparent font-mono text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
      />
    </div>
  );
}
